import { deviceIsDesktop } from "../common/checkDevice";

function appendSignature(root) {
  const textInner = root.querySelector(".article_block_text_inner");
  if (!textInner) return;

  // 🔑 If a signature container already exists, do NOT add another one
  if (textInner.querySelector(".video-signature__container")) return;

  const html = `
    <div class="video-signature__container" aria-hidden="true">
      <img
        class="scene6-crown"
        alt="Signature"
        decoding="async"
        loading="eager"
        src="/custom/images/animations-image-seq/signature/00.webp"
      >
    </div>
  `;
  textInner.insertAdjacentHTML("beforeend", html);
}
export function imageSlideScene() {
  const root = document.querySelector(".scene-3");
  if (!root) return;

  // avoid double-binding of JS logic
  if (root.dataset.bound === "1") return;
  root.dataset.bound = "1";

  appendSignature(root);
  gsap.registerPlugin(ScrollTrigger);

  const h4 = root.querySelector("h4");
  if (!h4) return;

  // h4 already split into .char in PHP
  gsap.set(root.querySelectorAll(".char"), { opacity: 0, y: 12 });
  gsap.set(h4, { opacity: 1 });

  const getParas = () => gsap.utils.toArray(".scene-3 .content_text p");

  const PX_PER_SEGMENT = 150;
  const EXTRA_SCROLL = 400;

  const buildThresholds = (arr) => {
    const total = arr.length * PX_PER_SEGMENT + EXTRA_SCROLL;
    const offset = 0.3 * PX_PER_SEGMENT; // start a bit into the first segment
    return arr.map((_, i) => (i * PX_PER_SEGMENT + offset) / total);
  };

  // ───────────────────────────────
  // Signature image-sequence setup
  // ───────────────────────────────

  // 🔑 Scope this to *this* scene only
  const sigImg = root.querySelector(".video-signature__container img.scene6-crown");

  const SIGNATURE_SEQ_CONFIG = {
    basePath: "/custom/images/animations-image-seq/signature/",
    totalFrames: 48, // 00..47
    fps: 24,
    pad: 2,
    ext: "webp",
  };

  let signatureSeq = null;

  if (sigImg && window.makeImageSequence) {
    // 🔑 IMPORTANT: remove static src so we don't have 2 layers fighting
    sigImg.removeAttribute("src");

    signatureSeq = window.makeImageSequence({
      target: sigImg,
      totalFrames: SIGNATURE_SEQ_CONFIG.totalFrames,
      fps: SIGNATURE_SEQ_CONFIG.fps,
      mode: "once",
      basePath: SIGNATURE_SEQ_CONFIG.basePath,
      pad: SIGNATURE_SEQ_CONFIG.pad,
      ext: SIGNATURE_SEQ_CONFIG.ext,
      paintInitial: true, // 🔑 let the sequence paint frame 00
      initialFrame: 0,
    });

    // no need to goto(0); paintInitial already did that
  }

  if (sigImg) {
    gsap.set(sigImg, {
      opacity: 0,
      y: 10,
      willChange: "transform,opacity",
    });
  }

  let signaturePlayed = root.dataset.scene3SignaturePlayed === "1";

  const playSignatureOnce = () => {
    if (signaturePlayed || !sigImg) return;
    signaturePlayed = true;
    root.dataset.scene3SignaturePlayed = "1";

    gsap.to(sigImg, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out",
      onComplete: () => {
        signatureSeq?.play?.();
      },
    });
  };
  // ───────────────────────────────
  // “Play once” bookkeeping
  // ───────────────────────────────

  function markPlayed() {
    root.dataset.scene3Played = "1";
  }

  function setFinalState() {
    const paras = getParas();

    // heading chars
    gsap.set(".scene-3 .char", { opacity: 1, y: 0 });

    // paragraphs
    gsap.set(paras, {
      opacity: 1,
      y: 0,
      pointerEvents: "auto",
    });

    // image block
    gsap.set(".scene-3 .article_block_item_img", {
      opacity: 1,
      y: 0,
      pointerEvents: "auto",
    });

    // signature
    if (sigImg) {
      gsap.set(sigImg, { opacity: 1, y: 0 });
      signatureSeq?.goto?.(SIGNATURE_SEQ_CONFIG.totalFrames - 1);
    }
  }

  // ───────────────────────────────
  // Breakpoint logic via matchMedia
  // ───────────────────────────────
  ScrollTrigger.matchMedia({
    // DESKTOP WIDTH (>= 768px)
    "(min-width: 768px)": () => {
      // 🔑 re-check on every entry
      const alreadyPlayed = root.dataset.scene3Played === "1";

      const isTouch = ScrollTrigger.isTouch === 1;
      const isDesktopDevice = deviceIsDesktop();
      const allowPin = isDesktopDevice && !isTouch;

      // If scene already fully played → just set final state; no re-animating
      if (alreadyPlayed) {
        setFinalState();
        return () => {};
      }

      // If we *don’t* want heavy stuff here (touch / iPad / etc),
      // use the lighter mobile-style flow even on wide screens
      if (!allowPin) {
        const paras = getParas();
        gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
        gsap.set(".scene-3 .article_block_item_img", {
          opacity: 0,
          y: 20,
          pointerEvents: "none",
        });

        const imgTl = gsap
          .timeline({
            scrollTrigger: {
              trigger: ".scene-3 .article_block_item_inner",
              start: "top 80%",
            },
          })
          .to(".scene-3 .article_block_item_img", {
            opacity: 1,
            y: 0,
            duration: 0.5,
            immediateRender: false,
            ease: "none",
          });

        const textTl = gsap
          .timeline({
            scrollTrigger: {
              trigger: ".scene-3 h4",
              start: "top center",
            },
          })
          .to(".scene-3 .char", {
            opacity: 1,
            duration: 0.05,
            stagger: 0.05,
            ease: "none",
            immediateRender: false,
          })
          .to(
            paras,
            {
              opacity: 1,
              y: 0,
              stagger: 0.1,
              pointerEvents: "auto",
              duration: 0.55,
              ease: "none",
              onComplete: () => {
                markPlayed();
              },
            },
            0
          );

        let io = null;
        if (sigImg && signatureSeq) {
          const cont = document.querySelector(".video-signature__container");
          if (cont) {
            gsap.set(sigImg, { opacity: 0, y: 10 });

            io = new IntersectionObserver(
              (entries) => {
                entries.forEach((e) => {
                  if (e.isIntersecting && e.intersectionRatio >= 0.5) {
                    playSignatureOnce();
                    io.disconnect();
                  }
                });
              },
              { threshold: [0.5] }
            );

            io.observe(cont);
          }
        }

        return () => {
          imgTl.scrollTrigger?.kill();
          imgTl.kill();
          textTl.scrollTrigger?.kill();
          textTl.kill();
          if (io) io.disconnect();
        };
      }

      // ===== REAL DESKTOP: pinned reveal one-by-one =====

      // ===== REAL DESKTOP: pinned reveal one-by-one =====
      let revealed = [];
      const parasInit = getParas();
      gsap.set(parasInit, { opacity: 0, y: 20, pointerEvents: "none" });
      revealed = new Array(parasInit.length).fill(false);

      const h4Tl = gsap.to(".scene-3 .char", {
        opacity: 1,
        duration: 0.05,
        stagger: 0.05,
        ease: "none",
        immediateRender: false,
        scrollTrigger: {
          trigger: ".scene-3",
          start: "top 70%",
          once: true,
        },
      });

      // 🔑 shared end function (no dependency on another ST)
      const getEndValue = () => {
        const arr = getParas();
        const total = (arr.length || 1) * PX_PER_SEGMENT + EXTRA_SCROLL;
        return `+=${total}`;
      };

      function revealOnce(i) {
        const paras = getParas();
        if (!paras.length) return;
        if (i < 0 || i >= paras.length) return;
        if (revealed[i]) return;
        revealed[i] = true;

        const p = paras[i];
        gsap.to(p, {
          opacity: 1,
          y: 0,
          pointerEvents: "auto",
          duration: 0.55,
          ease: "none",
        });
      }

      const pinST = ScrollTrigger.create({
        trigger: ".scene-3",
        start: "top top+=50",
        end: getEndValue, // ✅ uses shared function
        pin: true,
        scrub: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onRefresh: () => {
          const paras = getParas();
          revealed = new Array(paras.length).fill(false);
          gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
        },
      });

      const progST = ScrollTrigger.create({
        trigger: ".scene-3",
        start: "top top+=50",
        end: getEndValue, // ✅ same function, no pinST.end
        scrub: true,
        onUpdate: (self) => {
          const paras = getParas();
          if (!paras.length) return;

          const thresholds = buildThresholds(paras);
          const prog = self.progress;

          for (let i = 0; i < thresholds.length; i++) {
            if (prog >= thresholds[i]) revealOnce(i);
          }

          if (thresholds.length && prog >= thresholds[thresholds.length - 1]) {
            playSignatureOnce();
            markPlayed();
          }
        },
        onRefresh: (st) => {
          if (st.isActive) {
            const paras = getParas();
            revealed = new Array(paras.length).fill(false);
            gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
          }
        },
      });

      return () => {
        h4Tl.scrollTrigger?.kill();
        h4Tl.kill();
        pinST.kill();
        progST.kill();
      };
    },

    // MOBILE WIDTH (< 768px)
    "(max-width: 767px)": () => {
      // 🔑 re-check on every entry
      const alreadyPlayed = root.dataset.scene3Played === "1";

      if (alreadyPlayed) {
        setFinalState();
        return () => {};
      }

      const paras = getParas();
      gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
      gsap.set(".scene-3 .article_block_item_img", {
        opacity: 0,
        y: 20,
        pointerEvents: "none",
      });

      const imgTl = gsap
        .timeline({
          scrollTrigger: {
            trigger: ".scene-3 .article_block_item_inner",
            start: "top 80%",
          },
        })
        .to(".scene-3 .article_block_item_img", {
          opacity: 1,
          y: 0,
          duration: 0.5,
          immediateRender: false,
          ease: "none",
        });

      const textTl = gsap
        .timeline({
          scrollTrigger: {
            trigger: ".scene-3 h4",
            start: "top center",
          },
        })
        .to(".scene-3 .char", {
          opacity: 1,
          duration: 0.05,
          stagger: 0.05,
          ease: "none",
          immediateRender: false,
        })
        .to(
          paras,
          {
            opacity: 1,
            y: 0,
            stagger: 0.1,
            pointerEvents: "auto",
            duration: 0.55,
            ease: "none",
            onComplete: () => {
              markPlayed();
            },
          },
          0
        );

      let io = null;
      if (sigImg && signatureSeq) {
        const cont = document.querySelector(".video-signature__container");
        if (cont) {
          gsap.set(sigImg, { opacity: 0, y: 10 });

          io = new IntersectionObserver(
            (entries) => {
              entries.forEach((e) => {
                if (e.isIntersecting && e.intersectionRatio >= 0.5) {
                  playSignatureOnce();
                  io.disconnect();
                }
              });
            },
            { threshold: [0.5] }
          );

          io.observe(cont);
        }
      }

      return () => {
        imgTl.scrollTrigger?.kill();
        imgTl.kill();
        textTl.scrollTrigger?.kill();
        textTl.kill();
        if (io) io.disconnect();
      };
    },
  });
}
// ============================ CHANGED FOR MATCH MEDIA END . WORKING CODE BELOW BEFORE CHANGE

// import { deviceIsDesktop } from "../common/checkDevice";

// function appendSignature() {
//   const html = `
//     <div class="video-signature__container" aria-hidden="true">
//       <img
//         class="scene6-crown"
//         alt="Signature"
//         decoding="async"
//         loading="eager"
//         src="/custom/images/animations-image-seq/signature/00.webp"
//       >
//     </div>
//   `;
//   document.querySelector(".scene-3 .article_block_text_inner")?.insertAdjacentHTML("beforeend", html);
// }

// export function imageSlideScene() {
//   const trigger = document.querySelector(".scene-3");
//   if (!trigger) return;

//   appendSignature();

//   const isDesktop = deviceIsDesktop();
//   gsap.registerPlugin(ScrollTrigger);

//   // Fade in image block
//   // gsap.to(".scene-3 .article_block_item_img", {
//   //   opacity: 1,
//   //   scrollTrigger: { trigger: ".scene-3", start: "top bottom" },
//   // });

//   const h4 = trigger.querySelector("h4");
//   if (!h4) return;

//   // h4 already split in PHP into .char spans
//   gsap.set(".scene-3 .char", { opacity: 0, y: 12 });
//   gsap.set(h4, { opacity: 1 });

//   const getParas = () => gsap.utils.toArray(".scene-3 .content_text p");

//   // Scroll distance per paragraph and some tail room
//   const PX_PER_SEGMENT = 150;
//   const EXTRA_SCROLL = 400;

//   // ❗ NEW: thresholds start after some scroll, not at 0
//   const buildThresholds = (arr) => {
//     const total = arr.length * PX_PER_SEGMENT + EXTRA_SCROLL;
//     // first paragraph reveals after ~0.3 of the first segment
//     const offset = 0.3 * PX_PER_SEGMENT;
//     return arr.map((_, i) => (i * PX_PER_SEGMENT + offset) / total);
//   };

//   // ───────────────────────────────
//   // Signature image-sequence setup
//   // ───────────────────────────────
//   const sigImg = document.querySelector(".video-signature__container img.scene6-crown");

//   const SIGNATURE_SEQ_CONFIG = {
//     basePath: "/custom/images/animations-image-seq/signature/",
//     totalFrames: 48, // 00..47
//     fps: 24,
//     pad: 2,
//     ext: "webp",
//   };

//   let signatureSeq = null;
//   if (sigImg && window.makeImageSequence) {
//     signatureSeq = window.makeImageSequence({
//       target: sigImg,
//       totalFrames: SIGNATURE_SEQ_CONFIG.totalFrames,
//       fps: SIGNATURE_SEQ_CONFIG.fps,
//       mode: "once",
//       basePath: SIGNATURE_SEQ_CONFIG.basePath,
//       pad: SIGNATURE_SEQ_CONFIG.pad,
//       ext: SIGNATURE_SEQ_CONFIG.ext,
//       paintInitial: false,
//       initialFrame: 0,
//     });
//     signatureSeq.goto?.(0);
//   }

//   if (sigImg) {
//     gsap.set(sigImg, {
//       opacity: 0,
//       y: 10,
//       willChange: "transform,opacity",
//     });
//   }

//   let signaturePlayed = false;
//   const playSignatureOnce = () => {
//     if (signaturePlayed) return;
//     signaturePlayed = true;
//     if (!sigImg) return;

//     gsap.to(sigImg, {
//       opacity: 1,
//       y: 0,
//       duration: 0.5,
//       ease: "power2.out",
//       onComplete: () => {
//         signatureSeq?.play?.();
//       },
//     });
//   };

//   // ───────────────────────────────
//   // Paragraph reveal bookkeeping
//   // ───────────────────────────────
//   let revealed = [];

//   function revealOnce(i) {
//     const paras = getParas();
//     if (!paras.length) return;
//     if (i < 0 || i >= paras.length) return;
//     if (revealed[i]) return;
//     revealed[i] = true;

//     const p = paras[i];
//     if (!p) return;

//     gsap.to(p, {
//       opacity: 1,
//       y: 0,
//       pointerEvents: "auto",
//       duration: 0.55,
//       ease: "none",
//     });
//   }

//   if (isDesktop) {
//     // Heading once, before paragraphs start revealing
//     gsap.to(".scene-3 .char", {
//       opacity: 1,
//       duration: 0.05,
//       stagger: 0.05,
//       ease: "none",
//       immediateRender: false,
//       scrollTrigger: {
//         trigger: ".scene-3",
//         start: "top 70%",
//         once: true,
//       },
//     });

//     // All paragraphs start hidden
//     gsap.set(getParas(), { opacity: 0, y: 20, pointerEvents: "none" });

//     // Main pin to create scroll room
//     const pinST = ScrollTrigger.create({
//       trigger: ".scene-3",
//       start: "top top+=50",
//       end: () => {
//         const arr = getParas();
//         return `+=${(arr.length || 1) * PX_PER_SEGMENT + EXTRA_SCROLL}`;
//       },
//       pin: true,
//       scrub: true,
//       anticipatePin: 1,
//       invalidateOnRefresh: true,
//       onRefresh: () => {
//         const paras = getParas();
//         revealed = new Array(paras.length).fill(false);
//         gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
//       },
//     });

//     // Watch pin progress and reveal as we scroll through sections
//     ScrollTrigger.create({
//       trigger: ".scene-3",
//       start: "top top+=50",
//       end: () => pinST.end,
//       scrub: true,
//       onUpdate: (self) => {
//         const paras = getParas();
//         if (!paras.length) return;

//         const thresholds = buildThresholds(paras);
//         const prog = self.progress;

//         for (let i = 0; i < thresholds.length; i++) {
//           if (prog >= thresholds[i]) revealOnce(i);
//         }

//         // trigger signature on last paragraph threshold
//         if (thresholds.length && prog >= thresholds[thresholds.length - 1]) {
//           playSignatureOnce();
//         }
//       },
//       onRefresh: (st) => {
//         if (st.isActive) {
//           const paras = getParas();
//           revealed = new Array(paras.length).fill(false);
//           gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
//         }
//       },
//     });
//   } else {
//     // ───────────
//     // Mobile flow
//     // ───────────
//     const paras = getParas();
//     gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
//     gsap.set(".scene-3 .article_block_item_img", { opacity: 0, y: 20, pointerEvents: "none" });

//     gsap
//       .timeline({
//         scrollTrigger: { trigger: ".scene-3 .article_block_item_inner", start: "top 80%" },
//       })
//       .to(".scene-3 .article_block_item_img", {
//         opacity: 1,
//         y: 0,
//         duration: 0.5,
//         immediateRender: false,

//         ease: "none",
//       });
//     gsap
//       .timeline({
//         scrollTrigger: { trigger: ".scene-3 h4", start: "top center" },
//       })
//       .to(".scene-3 .char", {
//         opacity: 1,
//         duration: 0.05,
//         stagger: 0.05,
//         ease: "none",
//         immediateRender: false,
//       })
//       .to(
//         paras,
//         {
//           opacity: 1,
//           y: 0,
//           stagger: 0.1,
//           pointerEvents: "auto",
//           duration: 0.55,
//           ease: "none",
//         },
//         0
//       );

//     if (sigImg && signatureSeq) {
//       const cont = document.querySelector(".video-signature__container");
//       if (cont) {
//         gsap.set(sigImg, { opacity: 0, y: 10 });

//         const io = new IntersectionObserver(
//           (entries) => {
//             entries.forEach((e) => {
//               if (e.isIntersecting && e.intersectionRatio >= 0.5) {
//                 if (!signaturePlayed) {
//                   signaturePlayed = true;
//                   gsap.to(sigImg, {
//                     opacity: 1,
//                     y: 0,
//                     duration: 0.45,
//                     ease: "power2.out",
//                     onComplete: () => {
//                       signatureSeq.play?.();
//                     },
//                   });
//                   io.disconnect();
//                 }
//               }
//             });
//           },
//           { threshold: [0.5] }
//         );

//         io.observe(cont);
//       }
//     }
//   }
// }

//
//
//
//
//
//
//
//
// code before changing video to images
// ============================
// ============================
// ============================
// ============================
// ============================
// ============================
// ============================
// ============================
// ============================
// ============================
// import { deviceIsDesktop } from "../common/checkDevice";

// function appendSignature() {
//   const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
//   const html = `
//     <div class="video-signature__container" aria-hidden="true">
//       <video id="video-signature" muted playsinline webkit-playsinline preload="auto" style="display:block;width:100%;height:auto;">
//         ${
//           isSafari
//             ? `

//              <source src="/custom/images/webm/signature-bg-dark.mp4" type="video/mp4">

//             `
//             : `<source src="/custom/images/webm/signature-no-pd.webm" type="video/webm">
//              <source src="/custom/images/webm/video-signature-safari.mov" type='video/mp4; codecs="hvc1"'>
//              <source src="/custom/images/webm/signature-bg-dark.mp4" type="video/mp4">
//              `
//         }
//       </video>
//     </div>`;
//   document.querySelector(".scene-3 .article_block_text_inner")?.insertAdjacentHTML("beforeend", html);
// }
// export function imageSlideScene() {
//   const trigger = document.querySelector(".scene-3");
//   if (!trigger) return;

//   appendSignature();

//   const isDesktop = deviceIsDesktop();

//   // function syncArticleBlockHeight() {
//   //   const scene = document.querySelector(".scene-3");
//   //   if (!scene) return;

//   //   const inner = scene.querySelector(".article_block_item_inner");
//   //   const text = scene.querySelector(".article_block_text_inner");

//   //   if (inner && text) {
//   //     const style = window.getComputedStyle(inner);

//   //     if (style.display === "flex") {
//   //       const textHeight = text.offsetHeight;
//   //       inner.style.height = `${textHeight}px`;
//   //     } else {
//   //       // Optional: Reset height if not flex
//   //       inner.style.height = "";
//   //     }
//   //   }
//   // }

//   // syncArticleBlockHeight();
//   // window.addEventListener("resize", syncArticleBlockHeight);

//   // keep your bottle slide + fades
//   // gsap.to(".scene-3 .article_block_item_img img", {
//   //   x: -200,
//   //   ease: "none",
//   //   scrollTrigger: {
//   //     trigger: ".scene-3",
//   //     start: "top bottom",
//   //     end: "bottom+=1800 top",
//   //     scrub: true,
//   //   },
//   // });
//   gsap.to(".scene-3 .article_block_item_img", {
//     opacity: 1,
//     scrollTrigger: { trigger: ".scene-3", start: "top bottom" },
//   });

//   const h4 = trigger.querySelector("h4");
//   if (!h4) return;

//   // const split = new SplitText(h4, { type: "chars" });
//   gsap.set(".scene-3 .char", { opacity: 0, y: 12 });
//   gsap.set(h4, { opacity: 1 });

//   // paragraphs are dynamic (re-query when needed)
//   const getParas = () => gsap.utils.toArray(".scene-3 .content_text p");

//   // thresholds are rebuilt from current paragraphs
//   const PX_PER_SEGMENT = 150;
//   const EXTRA_SCROLL = 400;
//   const buildThresholds = (arr) => {
//     const total = arr.length * PX_PER_SEGMENT + EXTRA_SCROLL;
//     return arr.map((_, i) => (i * PX_PER_SEGMENT) / total);
//   };

//   // signature one-shot
//   let signaturePlayed = false;
//   const playSignatureOnce = () => {
//     if (signaturePlayed) return;
//     signaturePlayed = true;
//     const video = document.getElementById("video-signature");
//     if (video) {
//       try {
//         video.currentTime = 0;
//       } catch {}
//       video.play?.().catch(() => {});
//     }
//   };

//   // reveal bookkeeping (will be resized on refresh)
//   let revealed = [];

//   // guarded reveal
//   function revealOnce(i) {
//     const paras = getParas();
//     if (!paras.length) return;
//     if (i < 0 || i >= paras.length) return;
//     if (revealed[i]) return;
//     revealed[i] = true;

//     const p = paras[i];
//     if (!p) return;

//     gsap.to(p, {
//       opacity: 1,
//       y: 0,
//       pointerEvents: "auto",
//       duration: 0.55,
//       ease: "none",
//     });
//   }

//   if (isDesktop) {
//     // Heading once
//     gsap.to(".scene-3 .char", {
//       opacity: 1,
//       duration: 0.05,
//       stagger: 0.05,
//       ease: "none",
//       immediateRender: false,
//       scrollTrigger: {
//         trigger: ".scene-3",
//         start: "top 70%",
//         once: true,
//       },
//     });

//     // initial paragraph state
//     gsap.set(getParas(), { opacity: 0, y: 20, pointerEvents: "none" });

//     // Main pin to create scroll room (end depends on live paragraph count)
//     const pinST = ScrollTrigger.create({
//       trigger: ".scene-3",
//       start: "top top+=50",
//       // markers: true,
//       end: () => {
//         const arr = getParas();
//         return `+=${(arr.length || 1) * PX_PER_SEGMENT + EXTRA_SCROLL}`;
//       },
//       pin: true,
//       scrub: true,
//       anticipatePin: 1,
//       invalidateOnRefresh: true,
//       onEnter: () => {
//         const arr = getParas();
//         if (arr.length) revealOnce(0);
//       },
//       onRefresh: () => {
//         // reset states on refresh
//         const paras = getParas();
//         revealed = new Array(paras.length).fill(false);
//         gsap.set(paras, { opacity: 0, y: 20, pointerEvents: "none" });
//       },
//     });

//     // Watch the pin’s progress and reveal as thresholds are crossed
//     ScrollTrigger.create({
//       trigger: ".scene-3",
//       start: "top top+=50",
//       end: () => pinST.end,
//       scrub: true,
//       onUpdate: (self) => {
//         const paras = getParas();
//         if (!paras.length) return;

//         const thresholds = buildThresholds(paras);
//         const prog = self.progress;

//         for (let i = 0; i < thresholds.length; i++) {
//           if (prog >= thresholds[i]) revealOnce(i);
//         }

//         // fire signature near last paragraph’s threshold (if any)
//         if (thresholds.length && prog >= thresholds[thresholds.length - 1]) {
//           playSignatureOnce();
//         }
//       },
//       onRefresh: (st) => {
//         // if already active after refresh, ensure first reveal happens
//         if (st.isActive) {
//           const paras = getParas();
//           revealed = new Array(paras.length).fill(false);
//           if (paras.length) revealOnce(0);
//         }
//       },
//     });
//   } else {
//     // — mobile: keep your original flow
//     const cont = document.querySelector(".video-signature__container");
//     const video = document.getElementById("video-signature");
//     if (cont && video) {
//       video.muted = true;
//       video.playsInline = true;
//       video.setAttribute("playsinline", "");
//       video.setAttribute("webkit-playsinline", "");
//       video.preload = "auto";

//       cont.style.opacity = "0";
//       cont.style.transition = "opacity 300ms ease";

//       const io = new IntersectionObserver(
//         (entries) => {
//           entries.forEach((e) => {
//             if (e.isIntersecting && e.intersectionRatio >= 0.5) {
//               cont.style.opacity = "1";
//               try {
//                 video.currentTime = 0;
//               } catch {}
//               const p = video.play?.();
//               if (p && p.catch) p.catch(() => {});
//             } else {
//               video.pause();
//             }
//           });
//         },
//         { threshold: [0, 0.5, 1] }
//       );
//       io.observe(cont);

//       const unlock = () => {
//         const p = video.play?.();
//         if (p && p.catch) p.catch(() => {});
//         window.removeEventListener("touchstart", unlock);
//         window.removeEventListener("click", unlock);
//       };
//       window.addEventListener("touchstart", unlock, { once: true });
//       window.addEventListener("click", unlock, { once: true });
//     }
//     gsap.set(getParas(), { opacity: 0, y: 20, pointerEvents: "none" });

//     gsap
//       .timeline({
//         scrollTrigger: { trigger: ".scene-3 h4", start: "top center" },
//       })
//       .to(".scene-3 .char", {
//         opacity: 1,
//         duration: 0.05,
//         stagger: 0.05,
//         ease: "none",
//         immediateRender: false,
//       })
//       .to(
//         ".scene-3 p",
//         {
//           opacity: 1,
//           y: 0,
//           stagger: 0.1,
//           pointerEvents: "auto",
//           duration: 0.55,
//           ease: "none",
//         },
//         0
//       );
//   }
// }
